// ink-component
import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';

import { buttonGroupTokens } from '../../../../tokens/components/ButtonGroup/tokens';
import { buttonTokens } from '../../../../tokens/components/Button/tokens';

import ButtonGroupButton, { ButtonElement } from '../ButtonGroupButton/ButtonGroupButton';
import ButtonGroupInput from '../ButtonGroupInput/ButtonGroupInput';

export interface ButtonGroupToggleProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** The label of the Toggle */
  label?: string;
  /** input type */
  inputType?: 'checkbox' | 'radio';
  /** ID of the Toggle Button */
  id?: string;
  /** HTML Input checked state and relationship with the ActiveTrigger style on the Button */
  checked?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const Label = styled.label`
  display: flex;
  z-index: 1;

  // Z-index elevation overrides for improved active visibility
  &:hover {
    z-index: 2;
  }

  &:focus,
  &:focus-within {
    z-index: 3;
  }

  ${({ theme }) =>
    theme.active &&
    css`
      z-index: 4;

      &:hover {
        z-index: 5;
      }

      &:focus-within {
        z-index: 6;
      }
    `}

  // Styling to buttons inside labels
  ${ButtonElement} {
    background-color: ${buttonGroupTokens.OutlineBackgroundColor};
    color: ${buttonGroupTokens.OutlineTextColorInitial};

    // Override Border Radius when inside ButtonToggle
    &:not(:only-child) {
      border-radius: ${buttonGroupTokens.CoreBorderRadiusCenter};
    }

    // Active styles override for Toggles (same as Button primary)
    ${({ theme }) =>
      theme.active &&
      css`
        background-color: ${buttonTokens.BackgroundColorPrimaryDefault};
        border-color: ${buttonTokens.BorderColorPrimaryDefault};
        color: ${buttonTokens.FontColorPrimaryDefault};

        &:hover {
          background-color: ${buttonTokens.BackgroundColorPrimaryHover};
          color: ${buttonTokens.FontColorPrimaryDefault};
        }
      `}
  }

  // Border Radius Override when label is the first element
  &:first-child > ${ButtonElement} {
    border-radius: ${buttonGroupTokens.CoreBorderRadiusHorizontalFirst};
  }

  // Border Radius Override when label is the last element
  &:last-child > ${ButtonElement} {
    border-radius: ${buttonGroupTokens.CoreBorderRadiusHorizontalLast};
  }

  // Border Radius reset when label is the only element inside button groups
  &:only-child > ${ButtonElement} {
    border-radius: ${buttonGroupTokens.CoreBorderRadiusDefault};
  }
`;

/**
 * @purpose A Toggle element for ButtonGroup
 * @group ButtonGroup
 * @HTMLType HTMLLabelElement
 */
function ButtonGroupToggle(props: ButtonGroupToggleProps) {
  const { className, style, label, id, inputType, checked, ...otherProps } = props;
  const [value, setValue] = React.useState(false);

  const handleChange = e => {
    setValue(!value);
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        active: checked,
      })}
    >
      <Label data-testid={props['data-testid']} key={id} id={id} {...otherProps} tabIndex={-1}>
        <ButtonGroupInput
          aria-checked={checked}
          checked={value}
          onChange={handleChange}
          type={inputType}
          tabIndex={-1}
        />
        <ButtonGroupButton type={checked ? 'primary' : 'default'}>{label}</ButtonGroupButton>
      </Label>
    </ThemeProvider>
  );
}

ButtonGroupToggle.defaultProps = {
  inputType: 'checkbox',
  checked: false,
};

ButtonGroupToggle.displayName = 'ButtonGroup.Toggle';

export default ButtonGroupToggle;
